import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import type { ReactNode } from "react";
import { Pressable, View } from "@-/com.npmjs.react-native";

export function SettingsRowGroup({
  children,
  footer,
  title,
}: {
  children: ReactNode;
  footer?: string;
  title?: string;
}) {
  return (
    <View className="mb-3 px-2.5">
      <Surface>
        {title ? (
          <SurfaceHeader>
            <SurfaceTitle className="pb-2">{title}</SurfaceTitle>
          </SurfaceHeader>
        ) : null}
        <SurfaceContent>{children}</SurfaceContent>
      </Surface>
      {footer ? <Text className="mt-2 px-1 text-xs text-muted-foreground">{footer}</Text> : null}
    </View>
  );
}

export function SettingsRowSeparator() {
  return <View className="h-px bg-border/60" />;
}

export function SettingsRowLabel({
  destructive = false,
  label,
  subtitle,
}: {
  destructive?: boolean;
  label: string;
  subtitle?: string;
}) {
  return (
    <View className="min-w-0 flex-1">
      <Text className={destructive ? "text-base text-destructive" : "text-base text-foreground"}>
        {label}
      </Text>
      {subtitle ? <Text className="mt-1 text-xs text-muted-foreground">{subtitle}</Text> : null}
    </View>
  );
}

export function SettingsRow({
  accessibilityLabel,
  accessibilityRole,
  accessibilityState,
  destructive,
  disabled,
  icon,
  label,
  onPress,
  subtitle,
  testID,
  trailing,
  value,
}: {
  accessibilityLabel?: React.ComponentProps<typeof Pressable>["accessibilityLabel"];
  accessibilityRole?: React.ComponentProps<typeof Pressable>["accessibilityRole"];
  accessibilityState?: React.ComponentProps<typeof Pressable>["accessibilityState"];
  /** Red label, for irreversible actions such as Delete Account. */
  destructive?: boolean;
  disabled?: boolean;
  icon?: IconName;
  label: string;
  onPress?: () => void;
  subtitle?: string;
  testID?: React.ComponentProps<typeof Pressable>["testID"];
  trailing?: ReactNode;
  value?: string;
}) {
  const content = (
    <View className="min-h-11 flex-row items-center px-3 py-2">
      {icon ? (
        // Monochrome badge: a `background`-colored box (white in light, black in dark) with a
        // `foreground`-colored glyph (black in light, white in dark) — so every settings icon reads
        // black-on-white / white-on-black instead of a per-row accent color.
        <View className="mr-3 size-7 items-center justify-center rounded-xs bg-background">
          <Icon className="text-foreground" name={icon} size={18} />
        </View>
      ) : null}
      <SettingsRowLabel destructive={destructive} label={label} subtitle={subtitle} />
      {value !== undefined ? (
        <Text className="mr-1 max-w-[70%] shrink text-right text-base text-muted-foreground">
          {value}
        </Text>
      ) : null}
      {trailing}
    </View>
  );

  if (onPress === undefined) {
    return content;
  }

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      accessibilityState={accessibilityState}
      android_ripple={{ color: "rgba(0,0,0,0.06)" }}
      className="active:bg-accent"
      disabled={disabled}
      onPress={onPress}
      testID={testID}
    >
      {content}
    </Pressable>
  );
}
